import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import usePaymentConfigurations from "../hooks/usePaymentConfigurations";
import { PaymentConfiguration, isConfigurationReady } from "../types/payments";

const STORAGE_KEY = "payments-selected-configuration";

/**
 * The id is stored as-is rather than JSON encoded: it is already a plain
 * string, and there is nothing to decode when it comes back.
 */
function readStoredConfigId(): string | undefined {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) return undefined;
  // Tolerates a value left by a build that JSON encoded it — including the
  // literal "undefined" that encoding an empty selection produces.
  if (stored === "undefined" || stored === "null") return undefined;
  return stored.startsWith('"') ? stored.slice(1, -1) : stored;
}

interface SelectedPaymentConfigurationValue {
  /**
   * Only the companies shown as "ready to use" in the configurations list: a
   * registration still missing documents or tax authority credentials has
   * never charged anyone, so there is nothing to show for it.
   */
  selectableConfigurations: PaymentConfiguration[];
  /** The company the payments account screens are currently showing. */
  configuration?: PaymentConfiguration;
  configId?: string;
  setConfigId: (configId: string) => void;
  loading: boolean;
}

/**
 * Default for the components rendered outside the provider: no selection, which
 * every consumer reads as "do not narrow anything".
 */
const SelectedPaymentConfigurationContext = createContext<
  SelectedPaymentConfigurationValue
>({
  selectableConfigurations: [],
  setConfigId: () => {},
  loading: false
});

/**
 * Holds which billing entity the payments account screens are showing.
 *
 * A professional can register more than one company, and every screen under the
 * payments account (summary and the three histories) has to talk about the same
 * one, so the selection lives here rather than in each page. It is persisted so
 * a reload does not silently jump back to another company.
 */
export function SelectedPaymentConfigurationProvider({
  children
}: PropsWithChildren<{}>) {
  const { configurations, loading } = usePaymentConfigurations();
  const [storedId, setStoredId] = useState<string | undefined>(
    readStoredConfigId
  );

  const setConfigId = useCallback((configId: string) => {
    window.localStorage.setItem(STORAGE_KEY, configId);
    setStoredId(configId);
  }, []);

  const selectableConfigurations = useMemo(
    () => configurations.filter(isConfigurationReady),
    [configurations]
  );

  // A stored id can point at a configuration that no longer exists, at one
  // belonging to a different account on a shared browser, or at one that is no
  // longer ready; falling back to the first one keeps the screens showing real
  // data instead of nothing.
  const configuration = useMemo(
    () =>
      selectableConfigurations.find(item => item.id === storedId) ??
      selectableConfigurations[0],
    [selectableConfigurations, storedId]
  );

  const value = useMemo(
    () => ({
      selectableConfigurations,
      configuration,
      configId: configuration?.id,
      setConfigId,
      loading
    }),
    [selectableConfigurations, configuration, loading, setConfigId]
  );

  return (
    <SelectedPaymentConfigurationContext.Provider value={value}>
      {children}
    </SelectedPaymentConfigurationContext.Provider>
  );
}

export const useSelectedPaymentConfiguration = () =>
  useContext(SelectedPaymentConfigurationContext);

/**
 * Appends the selected company to a page title, so every screen of the payments
 * account says which company it is showing. Returns the title untouched while
 * there is no configuration to name.
 */
export function useCompanyPageTitle() {
  const { configuration } = useSelectedPaymentConfiguration();

  return useCallback(
    (title: string) =>
      configuration ? `${title} - ${configuration.organizationName}` : title,
    [configuration]
  );
}
